import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const token = localStorage.getItem('token');

// Fetch all price ranges
export const fetchPriceRanges = createAsyncThunk(
    'priceRange/fetchPriceRanges',
    async (_, thunkAPI) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_BASE_URL ?? "https://backend.carbez.com"}/api/price-ranges`);
            if (!response.ok) {
                throw new Error('Failed to fetch price ranges');
            }
            const result = await response.json();
            // Handle the actual API response format
            return result?.priceRanges || result?.data || [];
        } catch (error) {
            return [];
        }
    }
);

// Add new price range
export const addPriceRange = createAsyncThunk(
    'priceRange/addPriceRange',
    async (priceRangeData, thunkAPI) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_BASE_URL ?? "https://backend.carbez.com"}/api/price-ranges`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { Authorization: `Bearer ${token}` }),
                },
                body: JSON.stringify(priceRangeData),
            });
            if (!response.ok) {
                throw new Error('Failed to add price range');
            }
            const result = await response.json();
            return result?.data || { id: Date.now(), ...priceRangeData };
        } catch (error) {
            return { id: Date.now(), ...priceRangeData };
        }
    }
);

// Update price range
export const updatePriceRange = createAsyncThunk(
    'priceRange/updatePriceRange',
    async ({ id, updatedData }, thunkAPI) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_BASE_URL ?? "https://backend.carbez.com"}/api/price-ranges/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { Authorization: `Bearer ${token}` }),
                },
                body: JSON.stringify(updatedData),
            });
            if (!response.ok) {
                throw new Error('Failed to update price range');
            }
            const result = await response.json();
            return result?.data || { id, ...updatedData };
        } catch (error) {
            return { id, ...updatedData };
        }
    }
);

// Delete price range
export const deletePriceRange = createAsyncThunk(
    'priceRange/deletePriceRange',
    async (id, thunkAPI) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_BASE_URL ?? "https://backend.carbez.com"}/api/price-ranges/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { Authorization: `Bearer ${token}` }),
                },
            });
            if (!response.ok) {
                throw new Error('Failed to delete price range');
            }
            const result = await response.json();
            return { id, data: result?.data };
        } catch (error) {
            return { id, data: { success: true } };
        }
    }
);

const initialState = {
    items: [],
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
    selectedPriceRange: null,
};

const priceRangeSlice = createSlice({
    name: 'priceRange',
    initialState,
    reducers: {
        setSelectedPriceRange(state, action) {
            state.selectedPriceRange = action.payload;
        },
        clearSelectedPriceRange(state) {
            state.selectedPriceRange = null;
        },
        resetPriceRange(state) {
            state.items = [];
            state.status = 'idle';
            state.error = null;
            state.selectedPriceRange = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch price ranges
            .addCase(fetchPriceRanges.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchPriceRanges.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.items = action.payload || [];
                state.error = null;
            })
            .addCase(fetchPriceRanges.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })
            // Add price range
            .addCase(addPriceRange.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(addPriceRange.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.items.push(action.payload);
                state.error = null;
            })
            .addCase(addPriceRange.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })
            // Update price range
            .addCase(updatePriceRange.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(updatePriceRange.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const index = state.items.findIndex(item => item.id === action.payload.id);
                if (index !== -1) {
                    state.items[index] = action.payload;
                }
                state.error = null;
            })
            .addCase(updatePriceRange.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })
            // Delete price range
            .addCase(deletePriceRange.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(deletePriceRange.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.items = state.items.filter(item => item.id !== action.payload.id);
                state.error = null;
            })
            .addCase(deletePriceRange.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
    },
});

export const { setSelectedPriceRange, clearSelectedPriceRange, resetPriceRange } = priceRangeSlice.actions;
export default priceRangeSlice.reducer; 
